/**
 * Utility functions for filter persistence across view changes and date navigation
 */

const FILTER_STORAGE_KEY = "sifio_filter_persistence";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Merges current available options with previously selected options that may no longer be available
 * @param {Array} currentOptions - Currently available options from data
 * @param {Array} selectedOptions - Previously selected options from persistence
 * @returns {Array} Merged options with availability status
 */
export const mergeOptionsWithPersisted = (currentOptions = [], selectedOptions = []) => {
  const currentValues = new Set(currentOptions.map(opt => opt.value));
  const mergedOptions = [...currentOptions];

  // Add previously selected options that are no longer available
  selectedOptions.forEach(selectedOption => {
    if (!currentValues.has(selectedOption.value)) {
      mergedOptions.push({
        ...selectedOption,
        isUnavailable: true, // Mark as unavailable for styling
      });
    }
  });

  return mergedOptions;
};

/**
 * Save filter state to localStorage
 * @param {Object} filterState - The filter state to save
 */
export const saveFiltersToStorage = (filterState) => {
  try {
    const dataToSave = {
      ...filterState,
      timestamp: Date.now(),
    };
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(dataToSave));
  } catch (error) {
    console.warn("Failed to save filters to localStorage:", error);
  }
};

/**
 * Load filter state from localStorage
 * @returns {Object|null} - The saved filter state or null if not found/expired
 */
export const loadFiltersFromStorage = () => {
  try {
    const savedData = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!savedData) return null;

    const parsedData = JSON.parse(savedData);
    const now = Date.now();

    // Check if data is expired
    if (parsedData.timestamp && (now - parsedData.timestamp) > CACHE_DURATION) {
      localStorage.removeItem(FILTER_STORAGE_KEY);
      return null;
    }

    // Remove timestamp before returning
    const { timestamp, ...filterState } = parsedData;
    return filterState;
  } catch (error) {
    console.warn("Failed to load filters from localStorage:", error);
    return null;
  }
};

/**
 * Clear filter state from localStorage
 */
export const clearFiltersFromStorage = () => {
  try {
    localStorage.removeItem(FILTER_STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear filters from localStorage:", error);
  }
};

/**
 * Validate and filter selected options against available options
 * This ensures that persisted selections are still valid even if options have changed
 * Returns the actual option objects from availableOptions to ensure proper object references
 * @param {Array} selectedOptions - Array of selected option objects
 * @param {Array} availableOptions - Array of currently available option objects
 * @returns {Array} - Filtered array of valid selected options with correct object references
 */
export const validateSelectedOptions = (selectedOptions = [], availableOptions = []) => {
  if (!Array.isArray(selectedOptions) || !Array.isArray(availableOptions)) {
    return [];
  }

  // Create a Map of available options by value for faster lookup and to get the actual objects
  const availableOptionsMap = new Map(availableOptions.map(option => [option.value, option]));

  // Get the selected values that are still available
  const validSelectedValues = selectedOptions
    .filter(selectedOption =>
      selectedOption &&
      selectedOption.value !== undefined &&
      availableOptionsMap.has(selectedOption.value)
    )
    .map(selectedOption => selectedOption.value);

  // Return the actual option objects from availableOptions to ensure proper object references
  return validSelectedValues.map(value => availableOptionsMap.get(value));
};

/**
 * Merge persisted filters with current available options
 * This function ensures that only valid options are restored
 * @param {Object} persistedFilters - The persisted filter state
 * @param {Object} availableOptions - Object containing arrays of available options
 * @returns {Object} - Validated filter state
 */
export const mergePersistedFilters = (persistedFilters, availableOptions) => {
  if (!persistedFilters || !availableOptions) {
    return {
      selectedProgram: [],
      selectedSurgeon: [],
      selectedRec: [],
    };
  }

  return {
    selectedProgram: validateSelectedOptions(
      persistedFilters.selectedProgram,
      availableOptions.programOptions || []
    ),
    selectedSurgeon: validateSelectedOptions(
      persistedFilters.selectedSurgeon,
      availableOptions.surgeonOptions || []
    ),
    selectedRec: validateSelectedOptions(
      persistedFilters.selectedRec,
      availableOptions.recOptions || []
    ),
  };
};

/**
 * Check if any filters are currently active
 * @param {Object} filters - Filter state object
 * @returns {boolean} - True if any filters are active
 */
export const hasActiveFilters = (filters) => {
  if (!filters) return false;
  
  return (
    (filters.selectedProgram && filters.selectedProgram.length > 0) ||
    (filters.selectedSurgeon && filters.selectedSurgeon.length > 0) ||
    (filters.selectedRec && filters.selectedRec.length > 0)
  );
};

/**
 * Create a debounced function for saving filters to localStorage
 * This prevents excessive localStorage writes during rapid filter changes
 * @param {Function} saveFunction - The function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} - Debounced function
 */
export const createDebouncedSave = (saveFunction, delay = 500) => {
  let timeoutId;
  
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => saveFunction(...args), delay);
  };
};

/**
 * Get filter summary for debugging/logging
 * @param {Object} filters - Filter state object
 * @returns {string} - Human readable filter summary
 */
export const getFilterSummary = (filters) => {
  if (!filters) return "No filters";

  const parts = [];

  if (filters.selectedProgram?.length > 0) {
    parts.push(`Programs: ${filters.selectedProgram.length}`);
  }

  if (filters.selectedSurgeon?.length > 0) {
    parts.push(`Surgeons: ${filters.selectedSurgeon.length}`);
  }

  if (filters.selectedRec?.length > 0) {
    parts.push(`Recommendations: ${filters.selectedRec.length}`);
  }

  return parts.length > 0 ? parts.join(", ") : "No active filters";
};

/**
 * Check if filters should be restored based on current state and available options
 * @param {Object} currentFilters - Current filter state
 * @param {Object} availableOptions - Available filter options
 * @returns {boolean} - True if filters should be restored
 */
export const shouldRestoreFilters = (currentFilters, availableOptions) => {
  // Check if we have any available options
  const hasOptions = (
    (availableOptions.programOptions && availableOptions.programOptions.length > 0) ||
    (availableOptions.surgeonOptions && availableOptions.surgeonOptions.length > 0) ||
    (availableOptions.recOptions && availableOptions.recOptions.length > 0)
  );

  // Check if current filters are empty
  const filtersEmpty = (
    (!currentFilters.selectedProgram || currentFilters.selectedProgram.length === 0) &&
    (!currentFilters.selectedSurgeon || currentFilters.selectedSurgeon.length === 0) &&
    (!currentFilters.selectedRec || currentFilters.selectedRec.length === 0)
  );

  return hasOptions && filtersEmpty;
};

/**
 * Debug function to log filter state and options for troubleshooting
 * @param {string} context - Context description (e.g., "PlanFilters", "HistoricalFilters")
 * @param {Object} filterState - Current filter state
 * @param {Object} availableOptions - Available options
 */
export const debugFilterState = (context, filterState, availableOptions) => {
  console.group(`🔍 Filter Debug - ${context}`);
  console.log('Current Filter State:', filterState);
  console.log('Available Options:', availableOptions);
  console.log('Filter Summary:', getFilterSummary(filterState));
  console.log('Should Restore:', shouldRestoreFilters(filterState, availableOptions));
  console.groupEnd();
};
